import React from 'react';
import { t } from '@lingui/macro';
import { Button, ButtonProps, useDisclosure } from '@cardboard-ui/react';
import { graphql, useFragment } from 'utils/graphClient';
import { ManageSpaceShortcutModal } from 'components/ManageSpaceShortcutModal';
import { ShortcutButton_node$key } from './__generated__/ShortcutButton_node.graphql';
import { generatePath } from 'react-router-dom';
import { EVENT_PATH } from 'utils/routes';
import useGeneratePath from 'apps/TenantApp/components/CanonicalRedirect/useGeneratePath';
import { ShortcutIcon } from 'components/icons';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';

const nodeFragment = graphql`
  fragment ShortcutButton_node on Node {
    __typename
    id
    ... on HasOwnerPermissionsInterface {
      ownershipSpace {
        id
        canModifySpaceShortcuts {
          value
        }
        ...ManageSpaceShortcutModal_space
      }
      ownershipEvent {
        id
      }
      ownershipFolders(last: 1) {
        nodes {
          id
        }
      }
    }
    ... on FolderNodeInterface {
      name
      publishState
    }
    ... on FolderItem {
      content {
        ... on File {
          id
          url
          mimeType
        }
      }
    }
    ... on Event {
      name
      publishState
      coverImage {
        url
      }
    }
  }
`;

interface ShortcutButtonProps extends ButtonProps {
  node: ShortcutButton_node$key;
  closeMenu?: () => void;
}

export const ShortcutButton = ({
  node: _node,
  closeMenu,
  ...props
}: ShortcutButtonProps) => {
  const node = useFragment(nodeFragment, _node);
  const activeLayout = useActiveLayout();
  const isDesktop = activeLayout === Layout.DESKTOP;

  const {
    isOpen: isOpenOverlay,
    onOpen: onOpenOverlay,
    onClose: onCloseOverlay,
  } = useDisclosure();
  const { generateFolderPath, generateFolderItemPath } = useGeneratePath();

  const isPublished = node.publishState === 'PUBLISHED';
  if (!isPublished || !node.ownershipSpace?.canModifySpaceShortcuts.value)
    return null;

  const defaultTitle = node.name;
  let defaultImageUrl;
  let defaultUrl = `https://${location.host}`;
  switch (node.__typename) {
    case 'Folder':
      defaultImageUrl = '';
      defaultUrl += generateFolderPath(node);
      break;
    case 'FolderItem':
      defaultImageUrl = node.content?.mimeType?.startsWith('image')
        ? node.content?.url
        : undefined;
      defaultUrl += generateFolderItemPath(node);
      break;
    case 'Event':
      defaultImageUrl = node.coverImage?.url;
      defaultUrl += generatePath(EVENT_PATH, { eventId: node.id });
      break;
  }

  const onOpen = () => {
    onOpenOverlay();
    if (isDesktop) {
      closeMenu && closeMenu();
    }
  };
  const onClose = () => {
    onCloseOverlay();
    if (!isDesktop) {
      closeMenu && closeMenu();
    }
  };

  return (
    <>
      <Button
        w="100%"
        size="sm"
        rightIcon={<ShortcutIcon />}
        justifyContent="space-between"
        onClick={() => {
          onOpen();
        }}
        {...props}
      >
        {t`Create shortcut`}
      </Button>

      <ManageSpaceShortcutModal
        space={node.ownershipSpace}
        defaultTitle={defaultTitle}
        defaultImageUrl={defaultImageUrl}
        defaultUrl={defaultUrl}
        isOpen={isOpenOverlay}
        onClose={onClose}
      />
    </>
  );
};
